import { useLayoutEffect, useState, type CSSProperties } from 'react';

/** Keep the inbox inside the visible viewport when browser chrome or the
 * software keyboard changes it. Pinch zoom retains the existing layout. */
export function useInboxViewport(enabled: boolean): { style?: CSSProperties; keyboardOpen: boolean } {
  const [viewport, setViewport] = useState<{ height: number; top: number; keyboardOpen: boolean } | null>(null);

  useLayoutEffect(() => {
    if (!enabled) {
      setViewport(null);
      return;
    }
    const visual = window.visualViewport;
    const update = () => {
      if (visual && visual.scale !== 1) return;
      const height = visual?.height ?? window.innerHeight;
      const keyboardOpen = window.innerHeight - height > 120;
      const top = keyboardOpen ? visual?.offsetTop ?? 0 : 0;
      setViewport(previous => previous?.height === height && previous.top === top && previous.keyboardOpen === keyboardOpen
        ? previous : { height, top, keyboardOpen });
    };
    update();
    visual?.addEventListener('resize', update);
    visual?.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    return () => {
      visual?.removeEventListener('resize', update);
      visual?.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [enabled]);

  return {
    style: enabled && viewport ? {
      '--inbox-height': `${viewport.height}px`,
      '--inbox-top': `${viewport.top}px`,
    } as CSSProperties : undefined,
    keyboardOpen: enabled && Boolean(viewport?.keyboardOpen),
  };
}
